import { useState, useEffect, useRef, useCallback } from "react";
import { ordersApi } from "@/lib/api";

interface TaskProgress {
  progress?: number | {
    percentage?: number;
    completed?: number;
    total?: number;
    [key: string]: unknown;
  };
  status?: string;
  title?: string;
  description?: string;
  steps?: Array<{
    id?: string;
    name?: string;
    description?: string;
    status?: string;
    progress?: number;
    order?: number;
    [key: string]: unknown;
  }>;
  activities?: Array<{
    id?: string;
    type?: string;
    description?: string;
    status?: string;
    createdAt?: string;
  }>;
  [key: string]: unknown;
}

interface UseTaskProgressOptions {
  orderId: string | null;
  pollingInterval?: number; // in milliseconds
  autoStart?: boolean;
  onComplete?: () => void;
}

export function useTaskProgress({
  orderId,
  pollingInterval = 5000,
  autoStart = true,
  onComplete,
}: UseTaskProgressOptions) {
  const [taskData, setTaskData] = useState<TaskProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasReached100, setHasReached100] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchTaskProgress = useCallback(async (isInitialLoad = false) => {
    if (!orderId) return;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError(null);

    try {
      const response = await ordersApi.getTask(orderId);

      // The API returns: {taskId, task: {...}, progress: {percentage, completed, total}, ...}
      let progressData: unknown = (response as any)?.progress;
      let taskData: unknown = response;

      // Handle response wrapping
      if (response && typeof response === 'object') {
        if ('task' in response) {
          taskData = (response as any).task;
          progressData = (response as any).progress;
        } else if ('data' in response) {
          taskData = (response as any).data;
          progressData = (response as any).data?.progress;
        }
      }

      // Store task data with progress attached
      const combinedData = {
        ...(typeof taskData === 'object' && taskData !== null ? taskData : {}),
        progress: progressData
      };

      setTaskData(combinedData as TaskProgress);

      // Check if progress reached 100%
      let progress = 0;
      if (typeof combinedData.progress === 'number') {
        progress = combinedData.progress;
      } else if (typeof combinedData.progress === 'object' && combinedData.progress !== null) {
        progress = (combinedData.progress as any).percentage ?? 0;
      }

      // Check if task status is complete/completed
      const taskStatus = (combinedData as any).status?.toLowerCase();
      const isStatusComplete = taskStatus === 'complete' || taskStatus === 'completed';

      // If status is complete, treat as 100% even if progress number is 0
      if (isStatusComplete && progress === 0) {
        progress = 100;
      }

      if (progress >= 100 && !hasReached100) {
        setHasReached100(true);
        stopPolling();
        if (onComplete) {
          onComplete();
        }
      }
    } catch (err) {
      console.error("Failed to fetch task progress:", err);
      if (isInitialLoad) {
        setError("Failed to load task data");
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  }, [orderId, hasReached100, onComplete]);

  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return; // Already polling

    fetchTaskProgress(true); // Initial fetch

    pollingIntervalRef.current = setInterval(() => {
      fetchTaskProgress(false);
    }, pollingInterval);
  }, [fetchTaskProgress, pollingInterval]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (autoStart && orderId) {
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [autoStart, orderId, startPolling, stopPolling]);

  // Extract progress value for convenience
  const getProgressValue = useCallback(() => {
    let progress = 0;
    if (typeof taskData?.progress === 'number') {
      progress = taskData.progress;
    } else if (typeof taskData?.progress === 'object' && taskData.progress !== null) {
      progress = (taskData.progress as any).percentage ?? 0;
    }
    return progress;
  }, [taskData]);

  return {
    taskData,
    loading,
    refreshing,
    error,
    progressValue: getProgressValue(),
    hasReached100,
    startPolling,
    stopPolling,
    refetch: () => fetchTaskProgress(false),
  };
}
